import { useEffect, useMemo, useState } from 'react'
import {
    CommonConstant,
    ErrorConstant,
    PaginationType,
    TemplateService,
} from 'utility'

export const GetTempleteList = (
    paginationType = PaginationType?.default,
    TemplateInternalType = null,
    initialFilter = null,
) => {
    const [data, setData] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    // initialFilter: e.g. the Template List's remembered SortBy, applied before the first request (no double load).
    const [filter, setFilter] = useState(() => ({
        PageNumber: 1,
        PageSize: CommonConstant.defaultPageSize,
        SearchText: '',
        SortBy: 'name',
        all: paginationType,
        ...(initialFilter || {}),
    }))
    const options = useMemo(
        () =>
            (data || []).map(ele => ({
                value: ele.Id,
                text: ele.TemplateName,
            })),
        [data],
    )

    useEffect(() => {
        let isMounted = true
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await TemplateService.templateList(
                    !filter.all
                        ? {
                              ...filter,
                              ...(TemplateInternalType && {
                                  TemplateInternalType,
                              }),
                          }
                        : {
                              ...filter,
                              PageSize: 5000000,
                              ...(TemplateInternalType && {
                                  TemplateInternalType,
                              }),
                          },
                )
                if (isMounted) {
                    setData(result.Items || [])
                    console.log(result, result.Items)
                    setTotalCount(result.TotalCount || 0)
                }
            } catch {
                if (isMounted) {
                    setError(ErrorConstant.default)
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        fetchData()

        return () => {
            isMounted = false
        }
    }, [filter])

    const pageChanged = page => {
        setFilter({
            ...filter,
            PageNumber: page,
        })
    }

    const refresh = () => {
        setFilter({
            ...filter,
        })
    }

    const filterChanged = filterData => {
        setFilter({
            ...filter,
            ...filterData,
        })
    }

    return {
        data,
        setData,
        options,
        error,
        loading,
        filter,
        totalCount,
        pageChanged,
        refresh,
        filterChanged,
    }
}

export const GetTempleteDetails = templateId => {
    const [data, setData] = useState('')
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(false)

    const fetchData = async () => {
        try {
            setLoading(true)
            const result = await TemplateService.getTemplateDetails(templateId)
            setData(result || {})
        } catch {
            setError(ErrorConstant.default)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (templateId) {
            fetchData()
        }
    }, [templateId])

    return { data, error, loading, setData }
}

export const GetTemplateTypeDetails = () => {
    const [data, setData] = useState([])
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await TemplateService.getTemplateType()
                setData(
                    (result?.Items || []).map(data => ({
                        value: data.Id,
                        text: data.TemplateType,
                        name: data.TemplateType,
                    })),
                )
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    return { data, error, loading }
}

export const GetCompanyTemplateList = () => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const options = useMemo(
        () =>
            data.map(ele => ({
                value: ele.TemplateId,
                text: ele.TemplateName,
                UniqueId: ele.UniqueId, // Include UniqueId in options
                TemplateUniqueId: ele.TemplateUniqueId, // Include TemplateUniqueId (GUID) in options
            })),
        [data],
    )

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const result = await TemplateService.getCompanyTemplate()
                setData(result || {})
            } catch {
                setError(ErrorConstant.default)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    return {
        data,
        loading,
        options,
        error,
        setData,
    }
}
